//TODO: need to change the socket ID to use UMP ID. curreently wheree clientUUID IS.

import { Socket } from "socket.io-client";
import { SocketService } from "./socket/index";
import { emitEvent as emitEventUtil } from "./socket/emitEvent";
import { registerSocketClient as registerSocketClientUtil } from "./socket/registerSocketClient";
import { onIncomingSocketEvent as onIncomingSocketEventUtil } from "./socket/onIncomingSocketEvent";
import { getElementData } from "./utils/dom";
import { captureUrlParameters } from "./utils/captureUrlParameters";
import { checkForExistingUMPInSessionStorage } from "./utils/preUMPUtils/checkForExistingUMPInSessionStorage";
import { getKismetClientId } from "./utils/getKismetClientId";
import { config } from "../config";
import { SessionTracker } from "./session/SessionTracker";
import {
  SESSION_KEY_PREFIX,
  createSession,
  updateSessionLastEvent,
  hasActiveClientSession,
} from "./session/sessionManager";
import {
  PRE_UMP_SESSION_KEY_PREFIX,
  storeEventInSessionStorage,
} from "./utils/preUMPUtils/storeEventInSessionStorage";
import { fetchPersonalOffer as fetchPersonalOfferUtil } from "./networking/fetchPersonalOffer";
import { fetchFirstUMPofPUOonUMPO } from "./networking/fetchFirstUMPofPUOonUMPO";
import { fetchInstagramProfile } from "./networking/fetchInstagramProfile";
import {
  SocketEventTypeEnum,
  SocketEventData,
} from "./socket/types/SocketEventTypeEnum";
import { updatePersonalUserOfferMetrics } from "./networking/metrics/updatePersonalUserOfferMetrics";
import { addUserMarketingProfileOfferMetrics } from "./networking/metrics/addUserMarketingProfileOfferMetrics";
import { updateUserMarketingProfileOffersMetrics } from "./networking/metrics/updateUserMarketingProfileOffersMetrics";
import { UserMarketingProfileOfferMetricTypeEnum } from "./networking/metrics/types/UserMarketingProfileOfferMetricTypeEnum";
import { PROFILE_STORAGE_KEY } from "../components/Forms/networking/otpService";

// Add this at the top level, outside the class
type MonitoringServiceSubscriber = (instance: MonitoringService) => void;
const subscribers: MonitoringServiceSubscriber[] = [];

class MonitoringService {
  private socket: Socket | null = null;
  private socketService: SocketService | null = null;
  private static instance: MonitoringService;
  private lastUrl: string = window.location.href;
  private personalUserOfferId: string | null = null; // Store UUID
  private sessionTracker: SessionTracker | null = null;
  private cachedOfferData: any = null; // Store offer data
  private eventListeners: { [key: string]: EventListener } = {};
  private sessionClientId: string = getKismetClientId();
  private session: any = null;
  private sessionUMPId: string | null = null;

  private constructor() {
    // console.debug("MonitoringService: Current origin:", window.location.origin);
    const UNIQUE_OFFER_ID = config.monitoring.unique_offer_id_param;
    const urlParams = new URLSearchParams(window.location.search);
    this.personalUserOfferId = urlParams.get(UNIQUE_OFFER_ID);

    // Initialize offer data cache first so we know if there is a personal user offer
    this.initializeOfferData().then(() => {
      // Track initial website visit and start session
      if (!hasActiveClientSession(this.sessionClientId)) {
        const session = createSession(this.sessionClientId);
        this.session = session;
        // the following event is only emitted if there is no active session
        // AKA, once per session
        this.emitEvent(SocketEventTypeEnum.WEBSITE_VISIT, {
          metricName: "websiteVisits",
          value: 1,
          sessionId: this.session.id,
        });

        // Initialize the session tracker if we have a personalUserOfferId
        if (this.personalUserOfferId) {
          this.sessionTracker = new SessionTracker(
            this.personalUserOfferId,
            (eventName, data) => this.emitEvent(eventName, data)
          );
        }
      } else {
        // Update the last event time for an existing session
        this.session = updateSessionLastEvent(this.sessionClientId);
      }
      // Always set up event listeners, regardless of socket status
      this.setupEventListeners();

      if (!config.socket.enable_sockets) {
        //console.debug("MonitoringService: Sockets Not Enabled, skipping");
        return;
      }

      if (!this.personalUserOfferId) {
        console.warn(
          "MonitoringService: No personal offer ID found in URL parameters"
        );
      } else {
        if (config.env === "development") {
          console.debug(
            "MonitoringService: Initialized with PUO ID:",
            this.personalUserOfferId
          );
        }
      }

      this.initializeSocket();
    });
  }

  private initializeSocket() {
    console.debug(
      "MonitoringService: Initializing connection to Socket Server"
    );
    // Create socket service
    this.socketService = new SocketService(this.personalUserOfferId);
    // Initialize socket with callback functions
    this.socket = this.socketService.initializeSocket(
      // Fetch personal offer callback
      () => this.fetchPersonalOffer(),
      // Capture URL parameters callback
      () => this.captureUrlParametersWrapper(),
      // Register client callback
      () => this.registerSocketClient()
    );
  }

  public static getMonitoringServiceInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  public static resetMonitoringServiceInstance(): void {
    if (MonitoringService.instance) {
      console.debug("Cleaning up existing MonitoringService instance");
      MonitoringService.instance.cleanupMonitoringServiceInstance();
    }
    MonitoringService.instance = new MonitoringService();

    // Notify all subscribers about the new instance
    subscribers.forEach((subscriber) => subscriber(MonitoringService.instance));
  }

  private cleanupMonitoringServiceInstance(): void {
    // Cleanup socket connection
    if (this.socketService) {
      this.socketService.disconnect();
      this.socketService = null;
      this.socket = null;
    }

    // Cleanup session tracker
    if (this.sessionTracker) {
      this.sessionTracker.cleanup();
      this.sessionTracker = null;
    }

    // Remove all event listeners
    document.removeEventListener("click", this.eventListeners["click"]);
    document.removeEventListener("submit", this.eventListeners["submit"]);
    document.removeEventListener("change", this.eventListeners["change"]);
    window.removeEventListener("scroll", this.eventListeners["scroll"]);

    // Clear the event listeners object
    this.eventListeners = {};

    // Reset cached data
    this.cachedOfferData = null;
    this.lastUrl = window.location.href;
  }

  private setupEventListeners() {
    // console.debug("MonitoringService: Setting up event listeners");

    // Monitor all clicks
    const clickHandler = async (e: Event) => {
      const target = (e as MouseEvent).target as HTMLElement;
      const elementData = getElementData(target);
      this.emitEvent(SocketEventTypeEnum.CLICK, {
        data: { elementData, value: 1 },
        sessionId: this.session.id,
        metricName: "click",
      });

      // Handle conversion tracking if attribution is configured
      if (window.KismetConfig?.attribution && this.personalUserOfferId) {
        const { selector, text } = window.KismetConfig.attribution;
        const targetText = target.textContent?.trim();

        // Check if element matches either selector OR text content
        const selectorMatches = selector && target.matches(selector);
        const textMatches = text && targetText === text.trim();

        if (selectorMatches || textMatches) {
          console.debug("MonitoringService: Attribution Match Detected", {
            element: target,
            elementText: targetText,
            attribution: window.KismetConfig.attribution,
            matchDetails: {
              selectorMatched: selectorMatches,
              textMatched: textMatches,
              selector: selector,
              expectedText: text,
            },
          });

          try {
            // Use cached offer data, fetch only if not available
            const offerData =
              this.cachedOfferData || (await this.fetchPersonalOffer());
            console.debug("MonitoringService: Offer Data", {
              offerData,
            });
            const conversionValueCents = offerData?.conversionValueCents || 0;
            console.debug("MonitoringService: Conversion Value", {
              conversionValueCents,
              offerData,
            });

            console.debug("MonitoringService: Tracking Conversion", {
              offerId: this.personalUserOfferId,
              conversionValueCents,
              offerData,
            });

            // Use emitEvent instead of direct updateOfferMetrics call
            this.emitEvent(SocketEventTypeEnum.CONVERSION, {
              value: 1,
              metricName: "conversionsTracked",
              conversionDetails: {
                selector: selector,
                matchedText: targetText,
                conversionValueCents: conversionValueCents,
              },
            });

            // If we have a conversion value, emit that too
            if (conversionValueCents > 0) {
              this.emitEvent(SocketEventTypeEnum.CONVERSION_VALUE, {
                value: conversionValueCents,
                metricName: "conversionValueCents",
              });
            }
          } catch (error) {
            console.error("Failed to process conversion:", error);
          }
        }
      }
    };
    document.addEventListener("click", clickHandler);
    this.eventListeners["click"] = clickHandler;

    // Monitor all form submissions
    const submitHandler = (e: Event) => {
      const form = (e as SubmitEvent).target as HTMLFormElement;
      this.emitEvent(SocketEventTypeEnum.FORM_SUBMIT, {
        sessionId: this.session.id,
        metricName: "formSubmit",
        metricValue: {
          action: form.action,
          method: form.method,
          id: form.id,
          className: form.className,
        },
      });
    };
    document.addEventListener("submit", submitHandler);
    this.eventListeners["submit"] = submitHandler;

    // Monitor all input changes
    const changeHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      const elementData = getElementData(target);
      this.emitEvent(SocketEventTypeEnum.INPUT_CHANGE, {
        metricName: "inputChange",
        value: 1,
        elementDetails: elementData,
      });
    };
    document.addEventListener("change", changeHandler);
    this.eventListeners["change"] = changeHandler;

    // Monitor scroll events (debounced)
    let scrollTimeout: NodeJS.Timeout;
    const scrollHandler = (e: Event) => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.emitEvent(SocketEventTypeEnum.SCROLL, {
          metricName: "scrollActivity",
          x: window.scrollX,
          y: window.scrollY,
          maxY: Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight
          ),
        });
      }, 150);
    };
    window.addEventListener("scroll", scrollHandler);
    this.eventListeners["scroll"] = scrollHandler;
  }

  public emitEvent(
    eventName: string | SocketEventTypeEnum,
    data: SocketEventData
  ): void {
    console.debug(`MonitoringService: Emitting event: [${eventName}]`);

    // Update the last event timestamp for the session
    this.session = updateSessionLastEvent(this.sessionClientId);

    // 1. Immediately emit socket event (existing functionality)
    this.emitSocketEvent(eventName, data);

    const metricName = data.metricName || eventName;
    const metricValue = data.value !== undefined ? data.value : 1;

    updatePersonalUserOfferMetrics({
      personalOfferId: this.personalUserOfferId!,
      [metricName]: metricValue,
    }).catch((error) => {
      if (config.env === "development") {
        console.error("Failed to update Personal User Offer metrics:", error);
      }
    });

    // 3. Check if we have a UserMarketingProfile for this hotel
    const hasUMP = checkForExistingUMPInSessionStorage();
    if (hasUMP) {
      // Set the UMP ID
      this.sessionUMPId = hasUMP.UMP_ID;
      // If UMP exists, send event to backend directly
      // Update personal user offer metrics
      if (this.personalUserOfferId) {
        const UMP_KEY = `${PROFILE_STORAGE_KEY}`;
        const UMPO_Added = JSON.parse(
          sessionStorage.getItem(UMP_KEY) || "{}"
        ).UMPO_Added;
        //get session info
        if (UMPO_Added) {
          //if session info is found and it says the UMPO exists, then we will use the update method rather than the add method
          //update user marketing profile offer metrics

          updateUserMarketingProfileOffersMetrics({
            personalUserOfferId: this.personalUserOfferId!,
            userMarketingProfileId: this.sessionUMPId!,
            [metricName]: metricValue,
          }).catch((error) => {
            console.error("Failed to update UMPO metrics:", error);
            // The error is caught and logged, but doesn't break the function
          });
        } else {
          console.log("UMPO_Added: false");
          // Instead of duplicating the UMPO creation logic here,
          // use our dedicated function to create UMPO with all stored events
          this.createUMPOWithStoredEvents(this.sessionUMPId!)
            .then(() => {
              console.log(
                "MonitoringService: UMPO created successfully from emitEvent flow"
              );
            })
            .catch((error) => {
              console.error(
                "MonitoringService: UMPO creation error from emitEvent flow:",
                error
              );
            });
        }
      } else {
        console.error("No personalUserOfferId found");
      }
    } else {
      // 4b. Otherwise, store in session storage for later processing
      storeEventInSessionStorage(eventName, data, this.sessionClientId);
    }
  }

  public async onBookNow() {
    console.log("MonitoringService: Book Now");
    this.emitEvent(SocketEventTypeEnum.CONVERSION, {
      metricName: "convertedAt",
      value: new Date(),
    });
  }

  public async onUrlChange(newUrl: string) {
    console.log("MonitoringService: URL changed:", newUrl);

    // Track page view metrics if we have a client UUID
    if (this.personalUserOfferId) {
      // Use emitEvent instead of direct updateOfferMetrics
      this.emitEvent(SocketEventTypeEnum.URL_CHANGE, {
        previousUrl: this.lastUrl,
        newUrl,
        title: document.title,
        metricName: "pagesViewed",
        value: 1,
      });
    }

    // Update last URL
    this.lastUrl = newUrl;
  }

  public onPageView(page: string) {
    this.emitEvent(SocketEventTypeEnum.PAGE_VIEW, {
      page,
      metricName: "pagesViewed",
      value: 1,
    });
  }

  public onNavigationClick(button: string, newBackground: string) {
    this.emitEvent(SocketEventTypeEnum.NAVIGATION_CLICK, {
      metricName: "linkClicks",
      button,
      newBackground,
    });
  }

  public onIllustrationChange(visible: boolean) {
    this.emitEvent(SocketEventTypeEnum.ILLUSTRATION_CHANGE, {
      metricName: "illustrationChange",
      visible,
    });
  }

  public onChatbotStateChange(state: Record<string, any>) {
    this.emitEvent(SocketEventTypeEnum.CHATBOT_STATE, {
      metricName: "chatbotState",
      ...state,
      timestamp: new Date().toISOString(),
    });
  }

  public onPuoView() {
    this.emitEvent(SocketEventTypeEnum.PUO_VIEW, {
      metricName: UserMarketingProfileOfferMetricTypeEnum.PUO_VIEWED,
      value: new Date(),
      personalUserOfferId: this.personalUserOfferId,
      timestamp: new Date().toISOString(),
    });
  }

  public onPuoClick() {
    this.emitEvent(SocketEventTypeEnum.PUO_CLICK, {
      metricName: "clickedAt",
      value: new Date(),
    });
  }

  public onIncomingSocketEvent(
    eventName: string,
    callback: (command: any) => void
  ): () => void {
    return onIncomingSocketEventUtil(this.socket, eventName, callback);
  }

  private registerSocketClient(): void {
    registerSocketClientUtil(this.socket, this.personalUserOfferId);
  }

  public async fetchPersonalOffer(): Promise<any> {
    const offerData = await fetchPersonalOfferUtil(
      this.personalUserOfferId,
      (eventName, data) => this.emitEvent(eventName, data)
    );

    // Update cache if offerData is returned
    if (offerData) {
      //Update the offer data with info about the user.
      const firstAssociatedUMP = await fetchFirstUMPofPUOonUMPO(
        this.personalUserOfferId
      );
      // Debug the UMP data
      console.log("First Associated UMP:", firstAssociatedUMP);

      if (firstAssociatedUMP) {
        // Add UMP data to offer data
        offerData.userMarketingProfile = firstAssociatedUMP;

        // Fetch Instagram profile data if we have a UMP
        if (firstAssociatedUMP.instagramId) {
          console.log("Found Instagram ID:", firstAssociatedUMP.instagramId);

          const instagramProfile = await fetchInstagramProfile(
            firstAssociatedUMP.instagramId,
            (eventName, data) => this.emitEvent(eventName, data)
          );

          if (instagramProfile) {
            // Add Instagram profile data to the UMP data
            offerData.userMarketingProfile.instagramProfile = instagramProfile;
          }
        } else {
          console.log("No Instagram ID found in UMP:", firstAssociatedUMP);
        }
      }
      this.cachedOfferData = offerData;
    }

    return offerData;
  }

  private async initializeOfferData() {
    try {
      // Fetch and cache the offer data
      this.cachedOfferData = await this.fetchPersonalOffer();
    } catch (error) {
      console.error("Failed to initialize offer data:", error);
    }
  }

  // Wrapper for the captureUrlParameters function
  private captureUrlParametersWrapper(): void {
    captureUrlParameters((eventName, data) => this.emitEvent(eventName, data));
  }

  private emitSocketEvent(eventName: string, data: any): void {
    emitEventUtil(this.socket, this.personalUserOfferId, eventName, data);
  }

  public static initialize(): MonitoringService {
    // Only reset if an instance already exists
    if (MonitoringService.instance) {
      console.debug("Cleaning up existing MonitoringService instance");
      MonitoringService.instance.cleanupMonitoringServiceInstance();
      MonitoringService.instance = new MonitoringService();

      // Notify all subscribers about the new instance
      subscribers.forEach((subscriber) =>
        subscriber(MonitoringService.instance)
      );
    } else {
      console.debug("Creating new MonitoringService instance");
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  /**
   * Sets the User Marketing Profile ID in the monitoring service
   * @param umpId The User Marketing Profile ID to set
   */
  public setUserMarketingProfileId(umpId: string): void {
    console.debug(`MonitoringService: Setting UMP ID to ${umpId}`);

    // Set the UMP ID
    this.sessionUMPId = umpId;

    // Update session storage with UMP ID
    const UMP_KEY = `${PROFILE_STORAGE_KEY}`;
    const UMP_DATA = JSON.parse(sessionStorage.getItem(UMP_KEY) || "{}");
    UMP_DATA.UMP_ID = umpId;
    sessionStorage.setItem(UMP_KEY, JSON.stringify(UMP_DATA));
  }

  /**
   * Creates a User Marketing Profile Offer with all stored pre-UMP events
   * Returns a Promise that resolves when UMPO is created successfully, or rejects with an error
   * @param umpId The User Marketing Profile ID
   * @returns Promise that resolves when successful, rejects with error details when failed
   */
  public createUMPOWithStoredEvents(umpId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log(
        `MonitoringService: Creating UMPO for UMP ID ${umpId} with stored events`
      );

      if (!this.personalUserOfferId) {
        const error = new Error("No personal user offer ID available");
        console.debug("MonitoringService: " + error.message);
        return reject(error);
      }

      // Check if UMPO has already been added
      const UMP_KEY = `${PROFILE_STORAGE_KEY}`;
      const UMP_DATA = JSON.parse(sessionStorage.getItem(UMP_KEY) || "{}");

      if (UMP_DATA.UMPO_Added) {
        console.log("MonitoringService: UMPO already exists for this UMP");
        return resolve(); // No error, just finish early
      }

      // Ensure the UMPO_Added flag is set to false (or removed) before creating a new UMPO
      // This prevents race conditions where the flag might be set incorrectly
      delete UMP_DATA.UMPO_Added;
      sessionStorage.setItem(UMP_KEY, JSON.stringify(UMP_DATA));

      // Get all stored pre-UMP events
      const Client_Session_Key = `${SESSION_KEY_PREFIX}${this.sessionClientId}`;
      const Client_Session_Data = JSON.parse(
        sessionStorage.getItem(Client_Session_Key) || "{}"
      );
      const preUMP_Session_Key = `${PRE_UMP_SESSION_KEY_PREFIX}${this.sessionClientId}_tmp_${Client_Session_Data?.pre_UMP_ID}`;
      const preUMPOSession = JSON.parse(
        sessionStorage.getItem(preUMP_Session_Key) || "{}"
      );

      console.debug(
        "MonitoringService: Found stored pre-UMP events",
        preUMPOSession
      );

      // Debug log all metrics with their types
      if (preUMPOSession?.metrics) {
        console.log(
          "MonitoringService: Metrics data types:",
          Object.entries(preUMPOSession.metrics).map(([key, value]) => ({
            key,
            value,
            type: value !== null ? typeof value : "null",
          }))
        );
      }

      // Fix date fields in metrics
      if (preUMPOSession?.metrics) {
        // Date fields that need proper formatting
        const dateFields = ["viewedAt", "clickedAt", "convertedAt"];

        // Fix each date field if it exists
        dateFields.forEach((field) => {
          if (field in preUMPOSession.metrics) {
            const val = preUMPOSession.metrics[field];
            if (typeof val === "string" && val.startsWith("0")) {
              // Remove '0' prefix and parse to date
              preUMPOSession.metrics[field] = new Date(
                val.substring(1)
              ).toISOString();
            }
          }
        });
      }

      // Create the payload with the fixed metrics
      const payload = {
        personalUserOfferId: this.personalUserOfferId,
        userMarketingProfileId: umpId,
        // Include all metrics from stored events
        ...preUMPOSession?.metrics,
      };

      // Log the payload for debugging
      console.log("MonitoringService: UMPO Creation Payload:", payload);

      // Create the UMPO with the payload
      addUserMarketingProfileOfferMetrics(payload)
        .then((response) => {
          // Log the full response for debugging
          console.log("MonitoringService: UMPO Creation Response:", response);

          if (response.success) {
            // Get the latest UMP_DATA in case it was modified elsewhere
            const latestUMP_DATA = JSON.parse(
              sessionStorage.getItem(UMP_KEY) || "{}"
            );
            // Mark UMPO as added
            latestUMP_DATA.UMPO_Added = true;
            sessionStorage.setItem(UMP_KEY, JSON.stringify(latestUMP_DATA));
            console.debug(
              "MonitoringService: UMPO created successfully with stored events"
            );

            // Clean up stored events
            sessionStorage.removeItem(preUMP_Session_Key);
            resolve(); // Resolve the promise
          } else {
            const errorMessage =
              response.error || "Failed to create UMPO (API returned failure)";
            // Log detailed error information including the original payload
            console.error("MonitoringService: UMPO Creation Failed:", {
              error: errorMessage,
              response: response,
              originalPayload: payload,
            });
            reject(new Error(errorMessage));
          }
        })
        .catch((error) => {
          console.error("MonitoringService: Exception creating UMPO", error);
          reject(error); // Reject with the caught error
        });
    });
  }
}

// Initialize the singleton
export const monitoring = MonitoringService.initialize();

// Add a subscription mechanism to ensure all imports get the latest instance
export function subscribeToMonitoringService(
  callback: MonitoringServiceSubscriber
): () => void {
  subscribers.push(callback);
  // Immediately call with current instance
  callback(MonitoringService.getMonitoringServiceInstance());

  // Return unsubscribe function
  return () => {
    const index = subscribers.indexOf(callback);
    if (index !== -1) {
      subscribers.splice(index, 1);
    }
  };
}

// Export a function to get the current instance (always up-to-date)
export function getMonitoringService(): MonitoringService {
  return MonitoringService.getMonitoringServiceInstance();
}
